// src/hooks/useAuth.ts
// Unified authentication hook for Central and legacy auth
// Provides a consistent interface regardless of auth source

import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import { baseApi } from '../api/baseApi';
import { realtimeClient } from '../realtime/socketClient';
import {
  resetBoot,
  setBootLoading,
  selectBootStatus,
  selectBootUser,
  selectBootCurrentWorkspace,
  selectBootOtherWorkspaces,
} from '../store/slices/bootContextSlice';
import type { SwitchContextWorkspace } from '../store/slices/bootContextSlice';
import {
  selectCurrentUser,
  selectCurrentToken,
  selectIsAuthenticated,
  logout as legacyLogout,
  setCredentials as setLegacyCredentials,
} from '../store/slices/authSlice';
import {
  selectCentralUser,
  selectCentralAccessToken,
  selectCentralRefreshToken,
  selectCurrentCentralWorkspace,
  selectCentralWorkspaces,
  selectIsCentralAuthenticated,
  centralLogout,
  setCentralCredentials,
  updateCentralAccessToken,
  selectCentralWorkspace as selectCentralWorkspaceAction,
} from '../store/slices/centralAuthSlice';
import { useCentralLoginMutation } from '../api/centralAuthApi';
import { selectWorkspaceViaBearer } from '../api/centralSuiteApi';
import { proactiveRefreshCentralToken } from '../api/baseApi';
import { useLoginMutation, useDevLoginMutation } from '../api/authApi';
import {
  showCentralAuth,
  showLegacyAuth,
  AUTH_MODE,
  HANDOFF_OIDC_CRM_ENABLED,
  initiateCentralLogout,
} from '../config/centralAuth';
import type { CentralWorkspace } from '../api/centralAuthApi';

/**
 * Unified user type that works with both Central and legacy auth.
 */
export interface UnifiedAuthUser {
  id: string;
  email: string;
  fullName: string | null;
  avatarUrl: string | null;
  activeWorkspaceId: string | null;
  workspaceName: string | null;
  role: string | null;
  authSource: 'central' | 'legacy';
}

/**
 * Auth state returned by useAuth hook.
 */
export interface AuthState {
  /** Whether user is authenticated (via either Central or legacy) */
  isAuthenticated: boolean;
  /** Whether auth has been initialized */
  isInitialized: boolean;
  /** Unified user object */
  user: UnifiedAuthUser | null;
  /** Current access token */
  token: string | null;
  /** Authentication source */
  authSource: 'central' | 'legacy' | null;
  /** Current workspace (Central only) */
  workspace: CentralWorkspace | null;
  /** Available workspaces (Central only) */
  workspaces: CentralWorkspace[];
  /** Whether Central auth is available */
  centralAuthAvailable: boolean;
  /** Whether legacy auth is available */
  legacyAuthAvailable: boolean;
  /** Current auth mode */
  authMode: typeof AUTH_MODE;
}

/**
 * Auth actions returned by useAuth hook.
 */
export interface AuthActions {
  /** Login via Central Identity */
  centralLogin: (email: string, password: string) => Promise<void>;
  /** Login via legacy LevelUp auth */
  legacyLogin: (email: string, password?: string) => Promise<void>;
  /** Dev login (legacy only, development mode) */
  devLogin: (email?: string) => Promise<void>;
  /** Logout from current auth source */
  logout: () => Promise<void>;
  /** Switch workspace (Central only) */
  switchWorkspace: (workspaceId: string) => Promise<void>;
  /** Refresh token (Central only) */
  refreshToken: () => Promise<void>;
}

/**
 * Map an RP/BFF boot-context workspace (`SwitchContextWorkspace`, from
 * `/auth/context`) into the `CentralWorkspace` shape that useAuth exposes via
 * `workspace`/`workspaces`. RP mode carries no browser token; this is a pure
 * projection of already-loaded boot state — it introduces no token and makes
 * no request. `role` widens from the optional boot string to `CentralRole`.
 */
function toCentralWorkspace(w: SwitchContextWorkspace | null): CentralWorkspace | null {
  if (!w) return null;
  return {
    id: w.id,
    name: w.name,
    slug: w.slug,
    type: w.type,
    role: (w.role ?? 'member') as CentralWorkspace['role'],
    isPrimary: w.isPrimary,
  };
}

/**
 * Unified authentication hook that handles both Central and legacy auth.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, logout, authSource } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <LoginPage />;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.fullName || user?.email}!</p>
 *       <p>Logged in via: {authSource}</p>
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): AuthState & AuthActions {
  const dispatch = useAppDispatch();

  // Legacy auth state
  const legacyUser = useAppSelector(selectCurrentUser);
  const legacyToken = useAppSelector(selectCurrentToken);
  const isLegacyAuthenticated = useAppSelector(selectIsAuthenticated);

  // Central auth state
  const centralUser = useAppSelector(selectCentralUser);
  const centralToken = useAppSelector(selectCentralAccessToken);
  const isCentralAuthenticated = useAppSelector(selectIsCentralAuthenticated);
  const centralWorkspace = useAppSelector(selectCurrentCentralWorkspace);
  const centralWorkspaces = useAppSelector(selectCentralWorkspaces);
  const centralRefreshToken = useAppSelector(selectCentralRefreshToken);

  // RP/BFF (OIDC Relying Party) auth state — H-2C2 WP-6.
  // When HANDOFF_OIDC_CRM_ENABLED is on, identity lives in a server-side crm_sid
  // session and the browser holds NO token, so the Bearer selectors above are all
  // empty. The authoritative identity + workspace come from bootContext, which
  // RpBootGate populates from GET /auth/context. useAuth is the single place that
  // normalizes this plane into the same shape Bearer/legacy produce — no consumer
  // (Profile, Members, Menu, …) needs to know which plane authenticated the user.
  const bootStatus = useAppSelector(selectBootStatus);
  const bootUser = useAppSelector(selectBootUser);
  const bootWorkspace = useAppSelector(selectBootCurrentWorkspace);
  const bootOtherWorkspaces = useAppSelector(selectBootOtherWorkspaces);
  const isRpAuthenticated =
    HANDOFF_OIDC_CRM_ENABLED && bootStatus === 'ready' && !!bootUser;

  // RTK Query mutations
  const [centralLoginMutation] = useCentralLoginMutation();
  const [legacyLoginMutation] = useLoginMutation();
  const [devLoginMutation] = useDevLoginMutation();

  // Determine auth source and state
  const authSource = useMemo((): 'central' | 'legacy' | null => {
    // RP/BFF is a Central plane — it reports 'central' so downstream consumers
    // (and SuiteShellAdapter's useAuthForShell) treat it identically to Bearer.
    if (isCentralAuthenticated || isRpAuthenticated) return 'central';
    if (isLegacyAuthenticated) return 'legacy';
    return null;
  }, [isCentralAuthenticated, isRpAuthenticated, isLegacyAuthenticated]);

  const isAuthenticated =
    isCentralAuthenticated || isRpAuthenticated || isLegacyAuthenticated;
  const token = centralToken || legacyToken;

  // Build unified user object
  const user = useMemo((): UnifiedAuthUser | null => {
    if (isCentralAuthenticated && centralUser) {
      return {
        id: centralUser.id,
        email: centralUser.email,
        fullName: centralUser.fullName,
        avatarUrl: centralUser.avatarUrl,
        activeWorkspaceId: centralWorkspace?.id || null,
        workspaceName: centralWorkspace?.name || null,
        role: centralWorkspace?.role || null,
        authSource: 'central',
      };
    }

    // RP/BFF: identity + workspace come from bootContext (no browser token).
    // Mapped into the SAME UnifiedAuthUser shape as Bearer, with authSource
    // 'central', so Profile/Members/Menu resolve the current user identically.
    if (isRpAuthenticated && bootUser) {
      return {
        id: bootUser.id,
        email: bootUser.email,
        fullName: bootUser.fullName,
        avatarUrl: bootUser.avatarUrl,
        activeWorkspaceId: bootWorkspace?.id ?? null,
        workspaceName: bootWorkspace?.name ?? null,
        role: bootWorkspace?.role ?? null,
        authSource: 'central',
      };
    }

    if (isLegacyAuthenticated && legacyUser) {
      return {
        id: legacyUser.id,
        email: legacyUser.email,
        fullName: legacyUser.fullName,
        avatarUrl: legacyUser.avatarUrl,
        activeWorkspaceId: legacyUser.activeWorkspaceId,
        workspaceName: legacyUser.workspaceName,
        role: legacyUser.role,
        authSource: 'legacy',
      };
    }

    return null;
  }, [
    isCentralAuthenticated,
    centralUser,
    centralWorkspace,
    isRpAuthenticated,
    bootUser,
    bootWorkspace,
    isLegacyAuthenticated,
    legacyUser,
  ]);

  // Central login action
  const centralLogin = useCallback(
    async (email: string, password: string) => {
      const result = await centralLoginMutation({ email, password }).unwrap();
      dispatch(
        setCentralCredentials({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken || '',
          expiresIn: result.expiresIn,
          user: result.user,
        })
      );
    },
    [centralLoginMutation, dispatch]
  );

  // Legacy login action
  const legacyLogin = useCallback(
    async (email: string, password?: string) => {
      const result = await legacyLoginMutation({ email, password }).unwrap();
      dispatch(setLegacyCredentials({ token: result.token, user: result.user }));
    },
    [legacyLoginMutation, dispatch]
  );

  // Dev login action (legacy only)
  const devLogin = useCallback(
    async (email?: string) => {
      const result = await devLoginMutation({ email }).unwrap();
      dispatch(setLegacyCredentials({ token: result.token, user: result.user }));
    },
    [devLoginMutation, dispatch]
  );

  // Logout action (handles both auth sources)
  const logout = useCallback(async () => {
    // Central logout covers BOTH Central auth planes:
    //   • Bearer mode: authenticated via a browser token → isCentralAuthenticated.
    //   • RP/BFF mode: authenticated via the server-side crm_sid session, so the
    //     browser holds no token and isCentralAuthenticated is false. Gate on
    //     HANDOFF_OIDC_CRM_ENABLED so Sign Out still runs — otherwise the button
    //     would no-op in RP mode. initiateCentralLogout destroys the RP session
    //     via POST /auth/logout (fail-closed) before the Central end-session redirect.
    if (HANDOFF_OIDC_CRM_ENABLED || isCentralAuthenticated) {
      // Clear Redux state first (initiateCentralLogout will clear localStorage)
      dispatch(centralLogout());

      // Destroy the RP session (RP mode) and/or Central SSO, then redirect to the
      // OIDC end-session endpoint. Fail-closed in RP/BFF mode: if the local
      // /auth/logout does not succeed, initiateCentralLogout throws and does not
      // redirect, leaving the session intact for retry.
      await initiateCentralLogout();
      return; // Browser will navigate away
    }

    if (isLegacyAuthenticated) {
      dispatch(legacyLogout());
    }
  }, [isCentralAuthenticated, isLegacyAuthenticated, dispatch]);

  // Switch workspace (Central only)
  // IMPORTANT: Resets boot context, RTK Query cache, AND WebSocket connection
  // to prevent cross-workspace data leakage. No page reload — BootGate re-runs switch-context.
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      if (!isCentralAuthenticated || !centralToken) {
        throw new Error('Workspace switching requires Central authentication');
      }

      // CRITICAL ORDER: The sequence below prevents stale-token 401s during workspace switch.
      // Every step must happen in this exact order.

      // Step 1: Block all RTK queries immediately.
      // resetBoot → bootStatus='idle', setBootLoading → bootStatus='loading'.
      // baseApi's boot gate will reject any queries while bootStatus !== 'ready'.
      dispatch(resetBoot());
      dispatch(setBootLoading());

      // Step 2: Clear ALL cached API data BEFORE changing the token.
      // This kills any pending auto-refetches that would fire with the old token.
      // Must happen while bootStatus='loading' so the gate blocks any re-subscription refetches.
      dispatch(baseApi.util.resetApiState());

      // Step 3: Get new token from Central via bearer-token select-workspace.
      // Uses the CURRENT (old) token to authenticate with Central.
      const result = await selectWorkspaceViaBearer(centralToken, workspaceId);

      // Step 4: Store new tokens in Redux + localStorage.
      // BootGate's useEffect depends on accessToken — when this changes, it will re-run boot.
      // But bootStatus is still 'loading', so the gate continues blocking queries.
      // CRITICAL: Pass refreshToken if Central returned one — the old refresh token
      // may be invalidated after workspace switch and using it would cause 400 invalid_grant.
      dispatch(
        selectCentralWorkspaceAction({
          workspace: {
            id: result.workspace.id,
            name: result.workspace.name,
            slug: result.workspace.slug,
            type: result.workspace.type as 'personal' | 'business',
            role: result.workspace.role as any,
            isPrimary: result.workspace.isPrimary,
          },
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresIn: result.expiresIn,
        })
      );

      // Step 5: Force WebSocket disconnect → reconnect → subscribe to new workspace.
      try {
        await realtimeClient.switchWorkspace(workspaceId);
      } catch (error) {
        console.warn('[useAuth] WebSocket reset failed during workspace switch:', error);
      }

      // Step 6: BootGate's useEffect will fire (accessToken changed → runBoot recreated)
      // → calls fetchSwitchContext(newToken) → dispatches setBootReady
      // → bootStatus='ready' → gate opens → children render → queries fire with NEW token
    },
    [isCentralAuthenticated, centralToken, dispatch]
  );

  // Refresh token (Central only) — uses Central-direct OIDC token endpoint
  const refreshToken = useCallback(async () => {
    if (!isCentralAuthenticated || !centralRefreshToken) return;

    try {
      const result = await proactiveRefreshCentralToken(centralRefreshToken);
      if (result) {
        dispatch(
          updateCentralAccessToken({
            accessToken: result.accessToken,
            expiresIn: result.expiresIn,
            refreshToken: result.refreshToken,
          })
        );
      } else {
        // Refresh failed — fail closed
        dispatch(centralLogout());
      }
    } catch {
      dispatch(centralLogout());
    }
  }, [isCentralAuthenticated, centralRefreshToken, dispatch]);

  return {
    // State
    isAuthenticated,
    isInitialized: true,
    user,
    token,
    authSource,
    // RP/BFF: project boot-context workspaces into the CentralWorkspace shape so
    // the workspace outputs are populated in every plane. Bearer/legacy unchanged.
    workspace: isRpAuthenticated ? toCentralWorkspace(bootWorkspace) : centralWorkspace,
    workspaces: isRpAuthenticated
      ? [toCentralWorkspace(bootWorkspace), ...bootOtherWorkspaces.map(toCentralWorkspace)].filter(
          (w): w is CentralWorkspace => w !== null,
        )
      : centralWorkspaces,
    centralAuthAvailable: showCentralAuth(),
    legacyAuthAvailable: showLegacyAuth(),
    authMode: AUTH_MODE,

    // Actions
    centralLogin,
    legacyLogin,
    devLogin,
    logout,
    switchWorkspace,
    refreshToken,
  };
}

export default useAuth;
